import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
  type JSX
} from "react";

import type { WebSectionId } from "byzantine/src/dbbl/businessLogic/modules/userDisplayPreferences/types";

type HiddenSections = Partial<Record<WebSectionId, boolean>>;

type DashboardHiddenSectionsContextValue = {
  hiddenSections: HiddenSections;
  // Useful for dashboard sections which resolve asynchronously and may end up
  // rendering nothing.
  setSectionHidden: (id: WebSectionId, hidden: boolean) => void;
};

const defaultValue: DashboardHiddenSectionsContextValue = {
  hiddenSections: {},
  setSectionHidden: () => {}
};

const DashboardHiddenSectionsContext =
  createContext<DashboardHiddenSectionsContextValue>(defaultValue);

interface DashboardHiddenSectionsProviderProps {
  children: ReactNode;
}

export const DashboardHiddenSectionsProvider = ({
  children
}: DashboardHiddenSectionsProviderProps): JSX.Element => {
  const [hiddenSections, setHiddenSections] = useState<HiddenSections>({});

  const setSectionHidden = useCallback((id: WebSectionId, hidden: boolean) => {
    setHiddenSections((current) => {
      if (Boolean(current[id]) === hidden) return current;
      return { ...current, [id]: hidden };
    });
  }, []);

  const contextValue = useMemo(
    () => ({ hiddenSections, setSectionHidden }),
    [hiddenSections, setSectionHidden]
  );

  return (
    <DashboardHiddenSectionsContext.Provider value={contextValue}>
      {children}
    </DashboardHiddenSectionsContext.Provider>
  );
};

export const useDashboardHiddenSections =
  (): DashboardHiddenSectionsContextValue =>
    useContext(DashboardHiddenSectionsContext);

export default DashboardHiddenSectionsContext;
